import { FC, useCallback } from 'react';
import axios from 'axios';
import { router } from '@inertiajs/react';

import { Button } from '@/Components/Button';
import { useToast } from '@/hook/useToast';
import { AppModalProps } from '@/types/modal.type';
import { ModalView } from '@/Components/ModalView';
import { FlickAnimation } from '@/Components/FlickAnimation';
import { FruitBlueberrySvg } from '@/Components/Icons/FruitBlueberrySvg';

type ArchiveFlowModalProps = AppModalProps<{
    flowId: number;
    flowName: string;
    shouldRedirectToViewMode?: boolean;
}>;

export const ArchiveFlowModal: FC<ArchiveFlowModalProps> = ({
    payload,
    onClose,
}) => {
    const { showToast, hideToast } = useToast();

    const onUndoArchive = useCallback(() => {
        return new Promise<null>((resolve, reject) => {
            axios
                .post(
                    route('api.internal.flows.unarchive', {
                        flowId: payload.flowId,
                    }),
                )
                .then(() => {
                    hideToast();
                    showToast({
                        autoClose: 5000,
                        variant: 'success',
                        label: `Flow ${payload.flowName} has been unarchived`,
                    });
                    router.reload();
                    resolve(null);
                })
                .catch((error) => {
                    hideToast();
                    showToast({
                        autoClose: 5000,
                        variant: 'danger',
                        label: 'Failed to undo archive.',
                    });
                    reject(error);
                });
        });
    }, [payload.flowId, payload.flowName]);

    const onArchive = useCallback(() => {
        onClose();

        return new Promise<null>((resolve, reject) => {
            axios
                .post(
                    route('api.internal.flows.archive', {
                        flowId: payload.flowId,
                    }),
                )
                .then(() => {
                    const onSuccess = () => {
                        showToast({
                            autoClose: 5000,
                            variant: 'success',
                            label: `Flow ${payload.flowName} has been archived`,
                            action: {
                                label: 'Undo',
                                href: '#',
                                onClick: (e) => {
                                    e.preventDefault();
                                    onUndoArchive();
                                },
                            },
                        });
                    };

                    if (payload.shouldRedirectToViewMode) {
                        router.visit(
                            route('flows.show', { flow: payload.flowId }),
                            { onFinish: onSuccess },
                        );
                    } else {
                        router.reload();

                        onSuccess();
                    }

                    resolve(null);
                })
                .catch((error) => {
                    const message =
                        error?.response?.data?.message ??
                        'Failed to archive flow.';
                    showToast({
                        autoClose: 5000,
                        variant: 'danger',
                        label: message,
                    });
                    reject(error);
                });
        });
    }, [payload.flowId, payload.flowName, onClose]);

    return (
        <ModalView
            maxWidth="sm"
            onClose={onClose}
            onSubmit={onArchive}
            contentContainerClassName="py-6 px-8 text-brand-ocean flex flex-col justify-center items-center">
            <div className="relative min-w-60 h-24 flex items-center justify-center">
                <FlickAnimation className="absolute z-10 bottom-5 left-11" />
                <div className="absolute bottom-3 right-16">
                    <FruitBlueberrySvg bgFill="white" className="w-14 h-14" />
                </div>
            </div>
            <div className="w-full text-body-text-15 text-left text-brand-ocean mb-4 mt-2">
                Ready to archive the Flow &ldquo;{payload.flowName}&rdquo;?
            </div>
            <div className="mt-2 w-full flex flex-col items-center justify-center gap-2">
                <Button
                    className="w-full h-9"
                    variant="primary"
                    onClick={onArchive}>
                    Archive
                </Button>
                <Button
                    className="w-full h-9"
                    variant="default"
                    onClick={onClose}>
                    Cancel
                </Button>
            </div>
        </ModalView>
    );
};
